import { cn } from '@/lib/utils'
import { ChipGlyph } from './UrgencyBanner'
import type { QuotaInterruptCopy } from './eventQuota'

/**
 * Ink "tracking paused" card for an account that used its whole monthly
 * data-point allowance (DESIGN.md Urgency Scale: ink = 100% events
 * interrupt). Rendered over a white wash of the page content; the data
 * underneath stays put, which is what "Still here" promises.
 */
export function EventQuotaInterrupt({
  copy,
  onCta,
  ctaDisabled,
  className,
}: {
  copy: QuotaInterruptCopy
  onCta: () => void
  ctaDisabled?: boolean
  className?: string
}) {
  return (
    <section
      role="alertdialog"
      aria-labelledby="quota-interrupt-title"
      className={cn(
        'w-full max-w-[600px] rounded-2xl bg-urgency-ink-bg p-5 shadow-[0_24px_48px_-12px_rgb(0_0_0/0.12)] sm:p-7',
        className,
      )}
    >
      <span className="inline-flex h-[22px] items-center gap-1.5 rounded-full bg-urgency-rust-accent px-2 text-xs font-semibold leading-4 text-white">
        <ChipGlyph kind="pause" className="size-2.5" />
        Tracking paused
      </span>
      <h2
        id="quota-interrupt-title"
        className="mt-4 text-[22px] font-semibold leading-7 tracking-[-0.02em] text-urgency-ink-fg"
      >
        {copy.title}
      </h2>
      <p className="mt-2 text-[13px] leading-[18px] text-white/60">{copy.body}</p>

      <div className="mt-6 flex flex-col gap-5 border-t border-white/[0.12] pt-5 sm:flex-row sm:gap-4">
        <QuotaList heading={copy.keptHeading} items={copy.kept} dot="bg-white/70" />
        <QuotaList heading={copy.pausedHeading} items={copy.paused} dot="bg-urgency-rust-accent" />
      </div>

      {copy.cta && (
        <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3.5">
          {copy.cta.href ? (
            <a
              href={copy.cta.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-11 shrink-0 items-center justify-center rounded-lg bg-white px-[18px] text-sm font-semibold text-urgency-ink-bg transition-colors hover:bg-white/90"
            >
              {copy.cta.label}
            </a>
          ) : (
            <button
              type="button"
              onClick={onCta}
              disabled={ctaDisabled}
              className="inline-flex h-11 shrink-0 items-center justify-center rounded-lg bg-white px-[18px] text-sm font-semibold text-urgency-ink-bg transition-colors hover:bg-white/90 disabled:opacity-60"
            >
              {copy.cta.label}
            </button>
          )}
          {copy.ctaNote && <p className="text-xs leading-4 text-white/55">{copy.ctaNote}</p>}
        </div>
      )}
    </section>
  )
}

function QuotaList({ heading, items, dot }: { heading: string; items: string[]; dot: string }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2.5">
      <p className="text-xs font-medium leading-4 text-white/55">{heading}</p>
      <ul className="flex flex-col gap-2.5">
        {items.map((item) => (
          <li key={item} className="flex items-center gap-2 text-[13px] leading-[18px] text-urgency-ink-fg">
            <span aria-hidden className={cn('size-1.5 shrink-0 rounded-full', dot)} />
            {item}
          </li>
        ))}
      </ul>
    </div>
  )
}
